import Phaser from 'phaser';
import { ClientGameState, PlayerPosition, GamePhase, Card as CardType, GAME_WIDTH } from '@hokm/shared';
import { Card } from '../../../objects/Card';
import { PLAYER_HAND_Y, PlayerPositionConfig } from '../../../config/positions';
import { RENDER_SCALE } from '../../../config';

export interface HandRendererCallbacks {
  getPositionConfig: () => Record<PlayerPosition, PlayerPositionConfig>;
  toDisplayPosition: (serverPosition: PlayerPosition) => PlayerPosition;
  canPlayCard: (card: CardType, state: ClientGameState) => boolean;
  getCardRandomOffset: (cardId: string) => { x: number; y: number; rotation: number };
  onCardClicked: (card: Card, cardData: CardType) => void;
}

export interface HandRendererState {
  pendingCardPlay: CardType | null;
  awaitingTrickCollection: boolean;
  discardMode: boolean;
  isDealing: boolean;
  dealtCardIds: Set<string>;
  opponentDealtCounts: Map<PlayerPosition, number>;
}

/**
 * Handles rendering of player's hand and opponent card backs.
 */
export class HandRenderer {
  private scene: Phaser.Scene;
  private callbacks: HandRendererCallbacks;

  // Player's hand cards
  private myHandCards: Card[] = [];

  // Opponent card backs by display position
  private opponentCards: Map<PlayerPosition, Phaser.GameObjects.Image[]> = new Map();

  constructor(scene: Phaser.Scene, callbacks: HandRendererCallbacks) {
    this.scene = scene;
    this.callbacks = callbacks;
    this.initOpponentCards();
  }

  private initOpponentCards(): void {
    this.opponentCards.set(PlayerPosition.NORTH, []);
    this.opponentCards.set(PlayerPosition.WEST, []);
    this.opponentCards.set(PlayerPosition.EAST, []);
  }

  /**
   * Get the current hand cards.
   */
  getHandCards(): Card[] {
    return this.myHandCards;
  }

  /**
   * Set playable state on all hand cards.
   */
  setHandCardsPlayable(playable: boolean): void {
    this.myHandCards.forEach((card) => card.setPlayable(playable));
  }

  /**
   * Remove a card from the hand array (for optimistic play).
   */
  removeCard(card: Card): void {
    const idx = this.myHandCards.indexOf(card);
    if (idx >= 0) {
      this.myHandCards.splice(idx, 1);
    }
  }

  /**
   * Render the player's hand.
   */
  renderHand(state: ClientGameState, handState: HandRendererState): void {
    // Clear existing cards
    this.myHandCards.forEach((card) => card.destroy());
    this.myHandCards = [];

    let cards = state.myHand;

    // Diagnostic logging
    if (state.myHand.length === 0 && state.phase === GamePhase.PLAYING) {
      console.error('[renderHand] Empty hand during PLAYING phase!', {
        phase: state.phase,
        myHandLength: state.myHand.length,
        trickNumber: state.trickNumber,
        isMyTurn: state.isMyTurn,
        currentTrickCards: state.currentTrick.cards.length,
      });
    }

    // During initial dealing phases, only show dealt cards
    const initialDealPhases = [GamePhase.READY_TO_DEAL, GamePhase.TRUMP_SELECTION, GamePhase.DEALING];
    const isInitialDealing = initialDealPhases.includes(state.phase);

    if (isInitialDealing && handState.dealtCardIds.size > 0) {
      cards = cards.filter((c) => handState.dealtCardIds.has(c.id));
    }

    // Filter out pending card for optimistic UI
    if (handState.pendingCardPlay) {
      cards = cards.filter((c) => c.id !== handState.pendingCardPlay!.id);
    }

    const cardSpacing = Math.min(60, (GAME_WIDTH - 200) / cards.length);
    const startX = GAME_WIDTH / 2 - ((cards.length - 1) * cardSpacing) / 2;

    // Arc parameters for curved hand
    const arcHeight = 15;
    const arcRotation = 0.08;

    cards.forEach((cardData, i) => {
      const offset = this.callbacks.getCardRandomOffset(cardData.id);
      const normalizedPos = cards.length > 1 ? (i / (cards.length - 1)) * 2 - 1 : 0;
      const arcY = arcHeight * (normalizedPos * normalizedPos);
      const arcRot = -arcRotation * normalizedPos;

      const card = new Card(this.scene, startX + i * cardSpacing + offset.x, PLAYER_HAND_Y + offset.y + arcY, cardData);
      card.setRotation(offset.rotation + arcRot);
      card.setDepth(i);

      // Highlight trump suit
      if (state.trumpSuit && cardData.suit === state.trumpSuit) {
        card.setTrump(true);
      }

      // Check playability
      const isPlayable =
        state.isMyTurn &&
        state.phase === GamePhase.PLAYING &&
        !handState.pendingCardPlay &&
        !handState.awaitingTrickCollection;
      const canPlay = isPlayable && this.callbacks.canPlayCard(cardData, state);
      const playable = canPlay || handState.discardMode;
      card.setPlayable(playable);

      card.on('cardClicked', (clickedCard: CardType) => {
        this.callbacks.onCardClicked(card, clickedCard);
      });

      this.myHandCards.push(card);
    });
  }

  /**
   * Render opponent card backs.
   */
  renderOpponentCards(state: ClientGameState, handState: HandRendererState): void {
    // Clear existing opponent cards
    this.opponentCards.forEach((cards) => {
      cards.forEach((img) => img.destroy());
    });
    this.opponentCards.set(PlayerPosition.NORTH, []);
    this.opponentCards.set(PlayerPosition.WEST, []);
    this.opponentCards.set(PlayerPosition.EAST, []);

    const myPosition = state.myPosition;
    state.players.forEach((player) => {
      if (player.position === myPosition) return;

      const displayPosition = this.callbacks.toDisplayPosition(player.position);
      let cardCount: number;

      if (handState.isDealing && handState.opponentDealtCounts.has(displayPosition)) {
        cardCount = handState.opponentDealtCounts.get(displayPosition) || 0;
      } else {
        cardCount = player.cardCount || 0;
      }

      if (cardCount === 0) return;

      const cards: Phaser.GameObjects.Image[] = [];
      const config = this.callbacks.getPositionConfig()[displayPosition].cards;
      const startOffset = ((cardCount - 1) * config.spacing) / 2;

      const arcHeight = 18;
      const arcRotation = 0.1;

      for (let i = 0; i < cardCount; i++) {
        const offset = this.callbacks.getCardRandomOffset(`opponent-${displayPosition}-${i}`);
        const normalizedPos = cardCount > 1 ? (i / (cardCount - 1)) * 2 - 1 : 0;
        const arcOffset = arcHeight * (normalizedPos * normalizedPos);
        const arcRot = -arcRotation * normalizedPos;

        let x: number;
        let y: number;

        if (config.direction === 'h') {
          const dx = -startOffset + i * config.spacing + offset.x;
          const dy = offset.y - arcOffset;

          if (config.rotation !== 0) {
            const cos = Math.cos(config.rotation);
            const sin = Math.sin(config.rotation);
            x = config.x + dx * cos - dy * sin;
            y = config.y + dx * sin + dy * cos;
          } else {
            x = config.x + dx;
            y = config.y + dy;
          }
        } else {
          const dx = offset.x + (displayPosition === PlayerPosition.WEST ? -arcOffset : arcOffset);
          const dy = -startOffset + i * config.spacing + offset.y;
          x = config.x + dx;
          y = config.y + dy;
        }

        const cardBack = this.scene.add.image(x, y, 'card-back');
        cardBack.setRotation(config.rotation + offset.rotation + arcRot);
        cardBack.setScale(0.5 / RENDER_SCALE);
        cardBack.setDepth(i);
        cards.push(cardBack);
      }

      this.opponentCards.set(displayPosition, cards);
    });
  }

  /**
   * Clear all cards with animation.
   */
  async clearAll(): Promise<void> {
    const animations: Promise<void>[] = [];

    // Clear player hand with fade down
    this.myHandCards.forEach((card) => {
      const promise = new Promise<void>((resolve) => {
        this.scene.tweens.add({
          targets: card,
          alpha: 0,
          y: card.y + 50,
          duration: 400,
          ease: 'Power2.easeIn',
          onComplete: () => {
            card.destroy();
            resolve();
          },
        });
      });
      animations.push(promise);
    });
    this.myHandCards = [];

    // Clear opponent cards with fade
    this.opponentCards.forEach((cards) => {
      cards.forEach((cardImg) => {
        const promise = new Promise<void>((resolve) => {
          this.scene.tweens.add({
            targets: cardImg,
            alpha: 0,
            duration: 400,
            ease: 'Power2.easeIn',
            onComplete: () => {
              cardImg.destroy();
              resolve();
            },
          });
        });
        animations.push(promise);
      });
    });
    this.opponentCards.set(PlayerPosition.NORTH, []);
    this.opponentCards.set(PlayerPosition.WEST, []);
    this.opponentCards.set(PlayerPosition.EAST, []);

    await Promise.all(animations);
  }

  destroy(): void {
    this.myHandCards.forEach((card) => card.destroy());
    this.myHandCards = [];
    this.opponentCards.forEach((cards) => cards.forEach((img) => img.destroy()));
    this.opponentCards.clear();
  }
}
